/**
 * sdkBiddingSectionHtml.js - HTML twin of proxy-server/sdkBidding/sectionMd.js for the exported
 * sign-off reports. Renders the server-provided rollup `sdkBidding` payload; when only the
 * browser fallback (validation/sdkBiddingSection.js) is available the section is labelled with
 * that source and omits the mapping-scoped columns (AC 48).
 *
 * Flag semantics are the proxy's, carried in `section.mode`: 'on' renders, 'shadow' does not
 * (payload kept for MCP / logs), absent payload renders nothing. Exported-report builders are
 * exempt from the App.css colour tokens, so inline colours are fine here.
 *
 * Win price is omitted on purpose (no captured field is cited yet). Demand health is labelled
 * "does not gate" and uses no pass/fail wording. Never throws; returns '' on any problem.
 */

import { COPY, buildSdkBiddingSection } from '../validation/sdkBiddingSection';

const LABEL = { meta: 'Meta', liftoff: 'Liftoff' };
const BREAK_LABEL = { token: 'token', adFetched: 'ad fetched', fill: 'fill' };

const s = (v) => (v == null ? '' : String(v));
const esc = (v) => s(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* Cell text: identical wording to sectionMd.js (the parity test compares them). */

export function tokenText(t) {
  if (!t) return '';
  switch (t.status) {
    case 'ok': return `ok · present on ${t.present} of ${t.total}`;
    case 'warn': {
      const bits = [];
      if (t.empty) bits.push(`${t.empty} empty`);
      if (t.absent) bits.push(`${t.absent} absent`);
      return `warn · ${bits.join(', ')} of ${t.total}`;
    }
    case 'observed': {
      const bits = [];
      if (t.present) bits.push(`present on ${t.present}`);
      if (t.empty) bits.push(`entry with empty signal on ${t.empty}`);
      if (t.absent) bits.push(`entry absent on ${t.absent}`);
      if (t.unknown) bits.push(`unknown on ${t.unknown}`);
      return `observed, not graded · ${bits.join(', ') || 'none'} of ${t.total}`;
    }
    case 'unknown': return t.copy || 'unknown';
    default: return t.copy || 'n/a';
  }
}

export function winsText(w) {
  if (!w) return '';
  if (w.status === 'usb') return COPY.usb;
  if (!w.readable) return 'unreadable (body not retained)';
  const tail = w.unreadable ? `; ${w.unreadable} unreadable (body not retained)` : '';
  return `${w.count} of ${w.readable} responses${tail}`;
}

export function fetchText(a) {
  if (!a) return '';
  if (a.status === 'usb') return COPY.usb;
  if (a.status === 'n/a') return a.copy || 'n/a';
  if (a.status === 'unknown') return a.copy || 'unknown';
  return `${a.status === 'warn' ? 'warn · ' : 'ok · '}${a.fetched} of ${a.wins} wins fetched`;
}

export function testModeText(t) {
  if (!t) return '';
  if (t.status === 'usb') return COPY.usb;
  if (t.status === 'n/a') return t.copy || 'n/a';
  const bits = [];
  if (t.gamOnly) bits.push(`GAM test, SDK not: ${t.gamOnly}`);
  if (t.sdkOnly) bits.push(`SDK test, GAM not: ${t.sdkOnly}`);
  if (t.status === 'ok') return `ok · consistent on ${t.consistent} of ${t.matched}`;
  return `warn · ${bits.join('; ')} of ${t.matched}`;
}

export function bundleText(b) {
  if (!b) return '';
  if (b.status === 'usb') return COPY.usb;
  if (b.status === 'warn') return `warn · mismatch on ${b.mismatch} of ${b.agree + b.mismatch + b.notRun} (${b.selection || 'n/a'} mapping)`;
  if (b.status === 'ok') return `ok · agrees on ${b.agree} of ${b.agree + b.mismatch + b.notRun}`;
  return b.copy || 'n/a';
}

export function fillText(f) {
  if (!f) return '';
  if (f.status === 'usb') return COPY.usb;
  if (f.status === 'n/a' || f.status === 'unknown') return f.copy || 'n/a';
  const names = [...new Set((f.pings || []).filter((p) => p.failed).map((p) => p.label))].join(', ');
  if (f.status === 'warn') return `warn · ${f.failed} of ${f.wins} wins: ${names}`;
  return `ok · filled on ${f.pinged - f.failed} of ${f.wins} wins`;
}

export function demandText(r) {
  if (r.demand === 'unavailable') return COPY.usb;
  if (r.demand === 'unreadable') return 'unreadable (body not retained)';
  return r.demand || '';
}

export function firstBreakText(r) {
  return r.firstBreak ? BREAK_LABEL[r.firstBreak] : (r.availability === 'usb' ? COPY.usb : 'none');
}

export function deltaText(d) {
  if (!d) return 'no previous build';
  const sign = (n) => (n > 0 ? `+${n}` : String(n));
  return `${sign(d.requests)} requests, ${sign(d.wins)} wins${d.newUnit ? ' (unit new)' : ''}`;
}

const day = (v) => s(v).slice(0, 10);
const oneLine = (v) => s(v).replace(/\r\n|\r|\n/g, ' ');

/**
 * Plain-text lines of the `placements` block, [{ kind: 'p' | 'li', text }]. Same wording as
 * proxy-server/sdkBidding/sectionMd.js placementLines (the parity test compares the arrays).
 * Never throws; [] for a missing block.
 */
export function placementsLines(p) {
  try {
    if (!p || typeof p !== 'object') return [];
    const out = [];
    const push = (kind, t) => out.push({ kind, text: oneLine(t) });
    if (p.copy) push('p', p.copy);
    if (p.state === 'usb') return out;
    const w = p.window && typeof p.window === 'object' ? p.window : null;
    if (p.importedAt) push('p', `Meta placement report imported ${day(p.importedAt)}${w ? ` (window ${s(w.from)} to ${s(w.to)}, ${s(p.platform || 'platform unknown')})` : ''}.`);
    const c = p.counts && typeof p.counts === 'object' ? p.counts : null;
    if (c && (p.state === 'ok' || p.state === 'stale')) {
      push('p', `Placements (${s(p.platform || 'platform unknown')}): ${Number(c.known) || 0} known, ${Number(c.unknown) || 0} unknown, ${Number(c.notSeen) || 0} not seen, ${Number(c.dead) || 0} dead.`);
    }
    push('p', `Placement chains: ${Number(p.chainChecked) || 0} checked, ${Number(p.chainPartial) || 0} ${COPY.placementsPartial}.`);
    for (const f of (Array.isArray(p.findings) ? p.findings : [])) {
      if (!f || typeof f !== 'object') continue;
      push('li', `${s(f.level)} · ${s(f.unit || 'all units')} · ${s(f.code)} · ${s(f.text)}${Number(f.count) > 1 ? ` (x${Number(f.count)})` : ''}`);
    }
    if (Array.isArray(p.suppressed) && p.suppressed.length) push('li', `${COPY.placementsSuppressed}: ${p.suppressed.map(s).join(', ')}`);
    if (p.androidNote) push('p', p.androidNote);
    if (p.formatNote) push('p', p.formatNote);
    return out;
  } catch { return []; }
}

/** True when a payload should be drawn: the proxy's `mode` is 'on'. Shadow and off draw nothing. */
export function sdkBiddingVisible(section) {
  return !!section && typeof section === 'object' && section.mode === 'on';
}

const TD = 'style="padding:4px 6px;border:1px solid #ddd;vertical-align:top;font-size:12px"';
const TH = 'style="padding:4px 6px;border:1px solid #ddd;background:#f5f5f5;text-align:left;font-size:12px"';

/**
 * sdkBiddingSectionFor({ payload, rows, fallbackMode, previous }) -> HTML.
 * Server answer wins: a present `payload` is rendered by its own `mode` and the browser copy
 * is never consulted. Only when there is no payload AND the caller opted in with
 * `fallbackMode` ('on' | 'shadow') is the local builder run, labelled browser-fallback.
 * (An absent payload also means flag=off or an older proxy, so the fallback is opt-in.)
 */
export function sdkBiddingSectionFor({ payload = null, rows = null, fallbackMode = null, previous = null } = {}) {
  try {
    if (payload && typeof payload === 'object') return sdkBiddingSectionHtml(payload);
    if (fallbackMode !== 'on' && fallbackMode !== 'shadow') return '';
    const local = buildSdkBiddingSection(rows, { mode: fallbackMode, previous });
    return sdkBiddingSectionHtml(local);
  } catch {
    return '';
  }
}

/**
 * sdkBiddingSectionHtml(section, { visible }) -> HTML string ('' when nothing to draw).
 * `visible` overrides the mode gate (a caller that opted into the fallback passes true).
 */
export function sdkBiddingSectionHtml(section, { visible } = {}) {
  try {
    if (!section || typeof section !== 'object') return '';
    if (!(visible === true || sdkBiddingVisible(section))) return '';
    const fallback = section.source === 'browser-fallback';
    const out = ['<div class="section" id="sdk-bidding"><div class="section-title">SDK bidding</div>'];
    if (fallback) {
      out.push('<p class="small" style="color:#854f0b">Source: computed in this browser because the proxy did not return an SDK bidding answer. Mapping-scoped columns (bundle) are left out.</p>');
    }
    if (section.error) {
      out.push(`<p class="small">Could not build this section: ${esc(section.error)}</p></div>`);
      return out.join('');
    }
    const m = section.mapping || {};
    if (m.source === 'pubsetting') {
      const bu = m.buyerUnits || { meta: 0, liftoff: 0 };
      out.push(`<p class="small">Unit mapping: captured from publisher settings for ${esc((m.appNames || []).join(', '))}; ${bu.meta} units mapped to Meta, ${bu.liftoff} to Liftoff.</p>`);
      if (m.selection && !fallback) out.push(`<p class="small">Mapping chosen by msid on ${m.selection.exact} requests (exact) and as the only mapping on ${m.selection.single} (single).</p>`);
    } else {
      out.push(`<p class="small">${esc(m.copy || COPY.noMapping)}</p>`);
      if (m.observationText) out.push(`<p class="small">Observed: ${esc(m.observationText)}.</p>`);
    }
    for (const b of ['meta', 'liftoff']) {
      if (section.buyers && section.buyers[b] && !section.buyers[b].present) out.push(`<p class="small">${LABEL[b]}: ${esc(COPY.notInSession)}</p>`);
    }

    const rows = Array.isArray(section.rows) ? section.rows : [];
    if (!rows.length) {
      out.push('<p class="small">No Meta or Liftoff unit was found in the GAM ad requests of this session.</p>');
    } else {
      const heads = ['Buyer', 'Unit', 'Token', 'Wins', 'Ad fetched', 'Test mode'];
      if (!fallback) heads.push('Bundle');
      heads.push('Fill', 'Render', 'First break', 'Demand');
      out.push(`<table style="border-collapse:collapse;width:100%"><thead><tr>${heads.map((h) => `<th ${TH}>${h}</th>`).join('')}</tr></thead><tbody>`);
      for (const r of rows) {
        const cells = [
          LABEL[r.buyer] || r.buyer, r.unit || '(all units)', tokenText(r.token), winsText(r.wins), fetchText(r.adFetched), testModeText(r.testMode),
        ];
        if (!fallback) cells.push(bundleText(r.bundle));
        cells.push(fillText(r.fill), 'Manual', firstBreakText(r), demandText(r));
        out.push(`<tr>${cells.map((c) => `<td ${TD}>${esc(c)}</td>`).join('')}</tr>`);
      }
      out.push('</tbody></table>');
      const findings = [];
      for (const r of rows) for (const f of (r.evidence || [])) findings.push({ r, f });
      if (findings.length) {
        out.push('<p class="small">Findings (warn at most; they do not change any criterion, <code>signable</code> or the blockers):</p><ul>');
        for (const { r, f } of findings) {
          if (fallback && f.code === 'sdk_bidding_bundle_mismatch') continue;
          out.push(`<li class="small">warn · ${esc(LABEL[r.buyer] || r.buyer)} · ${esc(r.unit || 'all units')} · <code>${esc(f.code)}</code> · ${esc(f.msg)}</li>`);
        }
        out.push('</ul>');
      } else {
        out.push('<p class="small">Findings: none.</p>');
      }
      out.push(`<p class="small">Manual: ${esc(COPY.render)}</p>`);
      if (rows.some((r) => r.buyer === 'meta')) out.push(`<p class="small">Manual: ${esc(COPY.metaAppId)}</p>`);
      const metaSide = rows.find((r) => r.buyer === 'meta' && r.bundle && r.bundle.metaSide);
      if (metaSide && !fallback) out.push(`<p class="small">Meta-side bundle: ${esc(metaSide.bundle.metaSide)}</p>`);
    }

    // Meta placements: advisory (warn or info), drawn before demand health; an older proxy omits the block.
    if (section.placements && typeof section.placements === 'object') {
      out.push('<h3 style="font-size:14px;margin:14px 0 6px">Meta placements</h3>');
      const lines = placementsLines(section.placements);
      // same order as the Markdown: paragraphs, then the finding list, then the trailing notes
      let i = 0;
      while (i < lines.length) {
        if (lines[i].kind === 'p') { out.push(`<p class="small">${esc(lines[i].text)}</p>`); i += 1; continue; }
        out.push('<ul>');
        while (i < lines.length && lines[i].kind === 'li') { out.push(`<li class="small">${esc(lines[i].text)}</li>`); i += 1; }
        out.push('</ul>');
      }
    }

    out.push(`<h3 style="font-size:14px;margin:14px 0 6px">${esc(section.demandHeader || COPY.demandHeader)}</h3>`);
    const demand = Array.isArray(section.demand) ? section.demand : [];
    if (!demand.length) {
      out.push('<p class="small">No Meta or Liftoff unit to count.</p>');
    } else {
      out.push(`<table style="border-collapse:collapse;width:100%"><thead><tr>${['Buyer', 'Unit', 'Live requests with token', 'Live wins', 'Wins by other buyers', 'Change vs previous build'].map((h) => `<th ${TH}>${h}</th>`).join('')}</tr></thead><tbody>`);
      for (const d of demand) {
        const wins = d.liveWins == null ? s(d.winsCopy || COPY.usb) : String(d.liveWins);
        const cells = [LABEL[d.buyer] || d.buyer, d.unit, d.liveRequests, wins, d.otherWins == null ? 0 : d.otherWins, deltaText(d.delta)];
        out.push(`<tr>${cells.map((c) => `<td ${TD}>${esc(c)}</td>`).join('')}</tr>`);
      }
      out.push('</tbody></table>');
    }
    out.push(`<p class="small">adtest rows excluded: ${section.adtestExcluded || 0}.</p>`);
    if (!(section.previousBuild && section.previousBuild.available)) {
      out.push(`<p class="small">${esc(COPY.noPrevious)}</p>`);
    }
    out.push('</div>');
    return out.join('');
  } catch {
    return '';
  }
}
